/**
 * Help Center overlay (FR-HELP-01, AC-HELP-001/002, AC-ARCH-REVIEW-003).
 *
 * Opened from the persistent "Help" nav entry and from the floating widget, on
 * every authenticated page, without a navigation. Three tabs — FAQs, Docs,
 * Contact Support — switch client-side: all three panels stay mounted, so
 * switching never refetches, never loses a half-typed support request, and
 * paints well inside the 300 ms p95 budget.
 *
 * Which tab is open lives in the URL (`?help=faqs|docs|contact`), so the
 * overlay survives a refresh and can be linked to. The shell derives its open
 * state from the same parameter.
 */
import { Link, useSearchParams } from 'react-router-dom';

import type { Persona } from '../lib/personas';
import { ContactSupportForm } from '../pages/help/contact-support-form';
import { DocsViewer } from '../pages/help/docs-viewer';
import { FaqTab } from '../pages/help/faq-tab';

import { OverlayDialog } from './overlay-dialog';

export const HELP_TABS = [
  { id: 'faqs', label: 'FAQs' },
  { id: 'docs', label: 'Docs' },
  { id: 'contact', label: 'Contact Support' },
] as const;

export type HelpTabId = (typeof HELP_TABS)[number]['id'];

export const DEFAULT_HELP_TAB: HelpTabId = 'faqs';

/** Narrows an arbitrary query value to a known tab. */
export function toHelpTab(value: string | null): HelpTabId | null {
  return HELP_TABS.some((tab) => tab.id === value) ? (value as HelpTabId) : null;
}

export interface HelpCenterOverlayProps {
  persona: Persona;
  activeTab: HelpTabId;
  onTabChange: (tab: HelpTabId) => void;
  onClose: () => void;
  /** Link target for "open the full docs site"; omitted outside a team portal. */
  docsHref?: string;
}

export function HelpCenterOverlay({
  persona,
  activeTab,
  onTabChange,
  onClose,
  docsHref,
}: HelpCenterOverlayProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const docsSlug = searchParams.get('help_doc');
  const docsSection = searchParams.get('help_section');
  const docsSearch = searchParams.get('help_docs_q') ?? '';
  const isAllRoles = searchParams.get('help_roles') === 'all';

  function patchParams(changes: Record<string, string | null>): void {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true }
    );
  }

  return (
    <OverlayDialog
      title="Help Center"
      onClose={onClose}
      headerExtra={<span className="badge">{persona.replace(/_/g, ' ')}</span>}
    >
      <div className="tablist" role="tablist" aria-label="Help Center sections">
        {HELP_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`help-tab-${tab.id}`}
            className="tablist__tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`help-panel-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="overlay__body">
        {/* All three panels stay mounted; only visibility changes, so switching
            tabs never reloads content or discards a draft. */}
        <TabPanel id="faqs" activeTab={activeTab}>
          <FaqTab persona={persona} />
        </TabPanel>
        <TabPanel id="docs" activeTab={activeTab}>
          <DocsViewer
            persona={persona}
            slug={docsSlug}
            sectionId={docsSection}
            search={docsSearch}
            isAllRoles={isAllRoles}
            variant="overlay"
            onSelect={(slug, sectionId) =>
              patchParams({ help_doc: slug, help_section: sectionId ?? null })
            }
            onSearchChange={(term) => patchParams({ help_docs_q: term })}
            onAllRolesChange={(next) => patchParams({ help_roles: next ? 'all' : null })}
            tocFooter={
              <Link
                className="btn btn--secondary btn--sm docs__full-link"
                to={docsHref ?? (docsSlug === null ? '/app/docs' : `/app/docs/${docsSlug}`)}
                onClick={onClose}
              >
                Open the full docs site
              </Link>
            }
          />
        </TabPanel>
        <TabPanel id="contact" activeTab={activeTab}>
          <ContactSupportForm />
        </TabPanel>
      </div>
    </OverlayDialog>
  );
}

function TabPanel({
  id,
  activeTab,
  children,
}: {
  id: HelpTabId;
  activeTab: HelpTabId;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      role="tabpanel"
      id={`help-panel-${id}`}
      aria-labelledby={`help-tab-${id}`}
      hidden={activeTab !== id}
    >
      {children}
    </div>
  );
}
